import * as THREE from 'three';
import { state, getCfg } from './state.js';
import { wakeRenderer } from './render-loop.js';

/* ═══════════════════════════════════════════
   CONDITIONAL ASSETS
   HDRI sky dome + BGM/SFX audio + Page Visibility API.
   All loads are fire-and-forget (.then/.catch) —
   none of these block the app from running.
   ═══════════════════════════════════════════ */

export function initAudioHdri(ctx) {
  const { scene, audioListener } = ctx;

  // ── HDRI Sky Dome ─────────────────────────
  if (getCfg('enableHdri') !== false) {
    const texLoader = new THREE.TextureLoader();
    texLoader.loadAsync('https://assets.plotos.in/global-assets/skies/universal_hdri.png')
      .then(tex => {
        tex.mapping = THREE.EquirectangularReflectionMapping;
        tex.colorSpace = THREE.SRGBColorSpace;
        scene.environment = tex;
        const skyGeo = new THREE.SphereGeometry(5000, 32, 32); // Lower poly: saves ~25K vertices on mobile
        const skyMat = new THREE.MeshBasicMaterial({ map: tex, side: THREE.BackSide, transparent: true, opacity: 0 });
        state.skyDome = new THREE.Mesh(skyGeo, skyMat);
        state.skyDome.position.y = 2700; // ← horizon offset. 0 = default. Negative = lower horizon (e.g. -1500 shows twilight band)
        scene.add(state.skyDome);
        state.skyLoaded = true;
        console.log('[Native Three.js] Sky HDRI loaded.');
      })
      .catch(e => console.warn('[Native Three.js] Non-critical asset (Sky HDRI) failed to load', e));
  }

  // ── Audio ─────────────────────────────────
  if (getCfg('enableMusic') !== false || getCfg('enableSfx') !== false) {
    const audioLoader = new THREE.AudioLoader();

    if (getCfg('enableMusic') !== false) {
      audioLoader.loadAsync('https://assets.plotos.in/global-assets/audio/universal_music.mp3')
        .then(buf => {
          state.bgmAudio = new THREE.Audio(audioListener);
          state.bgmAudio.setBuffer(buf);
          state.bgmAudio.setLoop(true);
          state.bgmAudio.setVolume(0.15);
          console.log('[Native Three.js] BGM loaded.');
        })
        .catch(e => console.warn('[Native Three.js] Non-critical asset (BGM) failed to load', e));

      const firstClickListener = () => {
        if (state.bgmAudio && !state.bgmAudio.isPlaying) {
          state.bgmAudio.setVolume(0);
          state.bgmAudio.play();
          let vol = 0;
          let fade = setInterval(() => {
            vol += 0.005;
            if (vol >= 0.15) { vol = 0.15; clearInterval(fade); }
            state.bgmAudio.setVolume(vol);
          }, 50); // Slow cinematic fade in
          document.removeEventListener('pointerdown', firstClickListener);
        }
      };
      document.addEventListener('pointerdown', firstClickListener);
    }

    if (getCfg('enableSfx') !== false) {
      audioLoader.loadAsync('https://assets.plotos.in/global-assets/audio/click_mp3.mp3')
        .then(buf => {
          state.sfxAudio = new THREE.Audio(audioListener);
          state.sfxAudio.setBuffer(buf);
          state.sfxAudio.setVolume(0.4);
          console.log('[Native Three.js] SFX loaded.');
        })
        .catch(e => console.warn('[Native Three.js] Non-critical asset (SFX) failed to load', e));
    }
  }

  // ── Page Visibility API: Smart Audio Lifecycle with 1.5s Fade ──
  document.addEventListener('visibilitychange', () => {
    if (state._bgmFadeInterval) { clearInterval(state._bgmFadeInterval); state._bgmFadeInterval = null; }

    if (document.hidden) {
      // Tab hidden — fade out over 1.5s then pause
      if (state.bgmAudio && state.bgmAudio.isPlaying) {
        state._bgmWasPlaying = true;
        state._bgmSavedVolume = state.bgmAudio.getVolume();
        let vol = state._bgmSavedVolume;
        const step = state._bgmSavedVolume / 30; // 30 ticks × 50ms = 1.5s
        state._bgmFadeInterval = setInterval(() => {
          vol -= step;
          if (vol <= 0) {
            vol = 0;
            state.bgmAudio.setVolume(0);
            state.bgmAudio.pause();
            clearInterval(state._bgmFadeInterval);
            state._bgmFadeInterval = null;
          } else {
            state.bgmAudio.setVolume(vol);
          }
        }, 50);
      }
    } else {
      // Tab visible — resume and fade in over 1.5s
      if (state.bgmAudio && state._bgmWasPlaying) {
        state._bgmWasPlaying = false;
        state.bgmAudio.setVolume(0);
        state.bgmAudio.play();
        let vol = 0;
        const target = state._bgmSavedVolume || 0.15;
        const step = target / 30;
        state._bgmFadeInterval = setInterval(() => {
          vol += step;
          if (vol >= target) {
            vol = target;
            state.bgmAudio.setVolume(target);
            clearInterval(state._bgmFadeInterval);
            state._bgmFadeInterval = null;
          } else {
            state.bgmAudio.setVolume(vol);
          }
        }, 50);
      }
      wakeRenderer();
    }
  });
}
